import { selectionConstants, tagConstants } from '../_constants';
import { stackUtilities } from '../_utilities';
import { tagActions } from './tag.actions';

export const selectionActions = {
  enter,
  exit,
  toggle,
  range,
  set,
  clear,
  prune,
  bulkTag,
  bulkUntag,
  undo,
  clearUndo,
};

function enter() {
  return { type: selectionConstants.ENTER };
}

function exit() {
  return { type: selectionConstants.EXIT };
}

function toggle(id) {
  return { type: selectionConstants.TOGGLE, id };
}

// `orderedIds` is the stack as the user currently sees it -- filtered and
// sorted -- because a shift-click range means "everything between these two
// on my screen", not between them in creation order.
function range(id, orderedIds) {
  return { type: selectionConstants.RANGE, id, orderedIds };
}

function set(ids) {
  return { type: selectionConstants.SET, ids };
}

function clear() {
  return { type: selectionConstants.CLEAR };
}

function prune(validIds) {
  return { type: selectionConstants.PRUNE, validIds };
}

function clearUndo() {
  return { type: selectionConstants.CLEAR_UNDO };
}

// --- reading the stores ---------------------------------------------

// A tag the server has not answered for yet. The placeholder does not resolve
// through the links table, so tagsForOrigami cannot see it; the queue can.
// This is the same test the tags sidebar makes before an untag.
function isPending(state, id, stackId) {
  const entry = state.tag.tagQueue[`${id}+${stackId}`];
  return !!entry && entry.type === tagConstants.TAG_REQUEST;
}

// Is this scan on this stack, counting a tag still in flight? Resolved through
// the links table the same way the sidebar resolves it, so the two can never
// disagree about what a scan carries.
function hasTag(state, id, stackId) {
  if (isPending(state, id, stackId)) return true;
  const origami = state.box.origamis.byId[id];
  return stackUtilities
    .tagsForOrigami(origami, state.stack.links.byId)
    .includes(stackId);
}

// The link to hand untag(). For a pending tag that is the placeholder, which
// is how the queue cancels a tag the server has not answered for yet -- so an
// undo pressed straight after a bulk tag still takes every one of them back.
function linkFor(state, id, stackId) {
  if (isPending(state, id, stackId)) return tagConstants.TAG_REQUEST;
  const origami = state.box.origamis.byId[id];
  if (!origami || !origami.origamiLinks) return null;
  const real = (origami.origamiLinks.items || []).find((linkId) => {
    const row = state.stack.links.byId[linkId];
    return row && row.stackId === stackId;
  });
  return real || null;
}

// The selected tag's items map, exactly as the sidebar hands it over:
// tagActions mutates the map it is given, and that map belongs to whichever
// tag is currently selected in the sidebar, so it is passed only when this IS
// that tag. Passing it for another tag would corrupt the sidebar's checkmarks.
function itemsFor(state, stackId) {
  return stackId === state.tag.id ? state.tag.items : null;
}

// How many of the selection a tag or an untag on `stackId` would touch, by the
// same rules bulkTag and bulkUntag apply, so the bulk bar's numbers are the
// numbers the operation reports back.
export function bulkCounts(state, stackId) {
  const { ids } = state.selection;
  if (!stackId) return { selected: ids.length, taggable: 0, untaggable: 0 };
  let tagged = 0;
  let present = 0;
  for (const id of ids) {
    if (!state.box.origamis.byId[id]) continue;
    present += 1;
    if (hasTag(state, id, stackId)) tagged += 1;
  }
  return { selected: ids.length, taggable: present - tagged, untaggable: tagged };
}

// --- bulk operations ------------------------------------------------
//
// Every bulk operation goes through the existing tag and untag thunks, never
// around them. The tag queue in those thunks is what reconciles a tag and an
// untag racing on the same pair (BZL-33, BZL-177), and a parallel path would
// bypass that reconciliation for exactly the case bulk creates: many requests
// in flight on one stack at once.
//
// Each thunk is built in the same expression that dispatches it. Since
// BZL-191 the thunks read tagQueue at dispatch, so building ahead would now be
// safe too; building in place keeps it that way without relying on it.

function bulkTag(stackId, ids) {
  return (dispatch, getState) => {
    const state = getState();
    const targets = (ids || state.selection.ids).filter(
      (id) => state.box.origamis.byId[id] && !hasTag(state, id, stackId)
    );
    const skipped = (ids || state.selection.ids).length - targets.length;
    const items = itemsFor(state, stackId);

    targets.forEach((id) => dispatch(tagActions.tag(id, stackId, items)));

    if (targets.length) {
      dispatch({
        type: selectionConstants.RECORD_UNDO,
        undo: { kind: 'tag', stackId, ids: targets },
      });
    }
    return { applied: targets.length, skipped };
  };
}

function bulkUntag(stackId, ids) {
  return (dispatch, getState) => {
    const state = getState();
    const items = itemsFor(state, stackId);
    let applied = 0;
    const undone = [];

    for (const id of ids || state.selection.ids) {
      if (!hasTag(state, id, stackId)) continue;
      const linkId = linkFor(state, id, stackId);
      if (!linkId) continue;
      dispatch(tagActions.untag(id, linkId, stackId, items));
      undone.push(id);
      applied += 1;
    }

    if (undone.length) {
      dispatch({
        type: selectionConstants.RECORD_UNDO,
        undo: { kind: 'untag', stackId, ids: undone },
      });
    }
    return { applied, skipped: (ids || state.selection.ids).length - applied };
  };
}

// Undo is the inverse operation over the same ids, resolved at undo time
// rather than recorded at apply time. A bulk tag's link ids do not exist until
// the server answers, so undoing a tag means "untag these scans as they stand
// now" -- and if the server has not answered yet, the untag finds the
// placeholder and the queue cancels the tag, which is the race the existing
// tests already pin.
function undo() {
  return (dispatch, getState) => {
    const op = getState().selection.undo;
    if (!op) return { applied: 0, skipped: 0 };
    dispatch(clearUndo());
    const inverse = op.kind === 'tag' ? bulkUntag : bulkTag;
    const result = dispatch(inverse(op.stackId, op.ids));
    // The inverse records its own undo; an undo of an undo is a redo, which
    // is more than was asked for and easy to trigger by accident.
    dispatch(clearUndo());
    return result;
  };
}
